import { useCallback, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { usePlayerData } from "./hooks/use-player-data";
import { usePercentiles } from "../../hooks/use-percentiles";
import { isBattingTab } from "../../utils/playerDataUtils";
import { STAT_TYPES } from "./constants";
import PlayerHeader from "./components/player-header/player-header";
import { PercentileSection } from "./components/percentile-rankings/percentile-rankings";
import { TabNavigation } from "./components/tab-navigation/tab-navigation";
import { PlayerContent } from "./components/player-content/player-content";
import { LoadingState, ErrorState } from "../../components/shared/page-states/page-states";

const getBaseStatType = (statType) => {
  const battingTabs = [
    STAT_TYPES.BATTING,
    STAT_TYPES.BASERUNNING,
    STAT_TYPES.BATTED_BALL,
    STAT_TYPES.SPLITS,
    STAT_TYPES.SITUATIONAL,
    STAT_TYPES.VALUE,
  ];
  return battingTabs.includes(statType) ? STAT_TYPES.BATTING : STAT_TYPES.PITCHING;
};

export const PlayerPage = () => {
  const { playerId } = useParams();
  const decodedPlayerId = useMemo(() => decodeURIComponent(playerId), [playerId]);

  const {
    playerData,
    playerYears,
    isLoading,
    isHeaderReady,
    setIsHeaderReady,
    error,
    activeTab,
    setActiveTab,
    selectedDivision,
    selectedConference,
    setSelectedConference,
    selectedYear,
    setSelectedYear,
    fallbackYearsByTab,
    availableTabs,
    statCategories,
    enhancedPlayerDataForPercentile,
  } = usePlayerData(decodedPlayerId);

  const {
    percentiles,
    isLoading: isPercentilesLoading,
    targetYear: percentTargetYear,
    prefetchYears,
  } = usePercentiles({
    playerId: decodedPlayerId,
    playerYears,
    activeTab,
    division: selectedDivision,
    conference: selectedConference,
    selectedYear,
    fallbackYearsByTab,
  });

  const handleYearChange = useCallback((year) => setSelectedYear(year), [setSelectedYear]);
  const handleConferenceChange = useCallback(
    (conf) => setSelectedConference(conf),
    [setSelectedConference]
  );

  const handleTabChange = useCallback(
    (newTab) => {
      if (newTab === activeTab) return;
      setActiveTab(newTab);

      if (playerYears) {
        const newTabType = isBattingTab(newTab) ? "batting" : "pitching";
        const mostRecentYear =
          newTabType === "batting"
            ? playerYears.most_recent_batting
            : playerYears.most_recent_pitching;
        if (mostRecentYear && mostRecentYear !== selectedYear) {
          setSelectedYear(mostRecentYear);
        }
      }
    },
    [activeTab, playerYears, selectedYear, setActiveTab, setSelectedYear]
  );

  // Sync percentile target year back to selected year
  useEffect(() => {
    if (percentTargetYear && selectedYear !== percentTargetYear) {
      setSelectedYear(percentTargetYear);
    }
  }, [percentTargetYear, selectedYear, setSelectedYear]);

  // Prefetch nearby years for percentiles
  useEffect(() => {
    if (!playerYears) return;
    const years = isBattingTab(activeTab)
      ? playerYears?.batting_years
      : playerYears?.pitching_years;
    if (!years?.length) return;
    const recent = [...years].sort((a, b) => b - a).slice(0, 3);
    prefetchYears(recent);
  }, [playerYears, activeTab, prefetchYears]);

  const currentPercentiles = useMemo(() => {
    const baseType = getBaseStatType(activeTab);
    return { [baseType]: percentiles[baseType] };
  }, [activeTab, percentiles]);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} title="Error Loading Player" />;
  if (!playerData) return <LoadingState />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="relative z-10 container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        {!isHeaderReady && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        )}

        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_5fr] gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <PlayerHeader
                playerData={playerData}
                onReady={() => setIsHeaderReady(true)}
              />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <PercentileSection
                playerData={{ ...enhancedPlayerDataForPercentile, playerYears }}
                initialPercentiles={currentPercentiles}
                activeTab={activeTab}
                selectedYear={selectedYear}
                onYearChange={handleYearChange}
                onConferenceChange={handleConferenceChange}
                isLoading={isPercentilesLoading}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <TabNavigation
              activeTab={activeTab}
              onTabChange={handleTabChange}
              availableTabs={availableTabs}
            />
            <PlayerContent
              key={decodedPlayerId}
              activeTab={activeTab}
              statCategories={statCategories}
              selectedDivision={selectedDivision}
              playerId={decodedPlayerId}
              playerData={playerData}
              selectedYear={selectedYear}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
